import React from 'react';
import classnames from 'classnames';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBooks } from '@fortawesome/pro-regular-svg-icons/faBooks';
import { ROUTES } from 'shared/constants';
import { useLang } from 'shared/hooks/lang.hooks';
import { toContentTypeChip } from 'shared/panda/messages/doc-answer';
import { useSourceName } from 'shared/panda/messages/source-name.hooks';
import type { RelatedResource } from 'shared/panda/messages/related-resources';
import { useKnowledgeItemsById } from 'shared/hooks/knowledge-items.hooks';
import type { KnowledgeItemRow } from 'shared/api/knowledge-rq.service.api';
import cardStyles from '../../panda-card.module.scss';
import renderStyles from '../render-messages/render-messages.module.scss';
import { SourceFooter } from '../answer-card/source-footer';
import styles from './related-resources.module.scss';

interface RelatedResourcesProps {
  /** The backend's lead-in ("I couldn't find a clear answer, but here are…"). */
  intro: string;
  resources: RelatedResource[];
}

// The no-answer box (Figma 1769:433): lead-in, then the server-ranked rows.
// One rows-by-id request for the whole list; a row's footer shows once its item lands.
export function RelatedResources({ intro, resources }: RelatedResourcesProps) {
  const { itemsById } = useKnowledgeItemsById(resources.map(resource => resource.documentId));
  return (
    <div className={classnames(cardStyles.card, styles.box)}>
      {intro && (
        <div className={renderStyles.intro}>
          <ReactMarkdown>{intro}</ReactMarkdown>
        </div>
      )}
      <ul className={styles.list}>
        {resources.map(resource => (
          <RelatedResourceRow
            key={resource.key}
            resource={resource}
            knowledgeItem={itemsById[resource.documentId]}
          />
        ))}
      </ul>
    </div>
  );
}

interface RelatedResourceRowProps {
  resource: RelatedResource;
  knowledgeItem?: KnowledgeItemRow;
}

function RelatedResourceRow({ resource, knowledgeItem }: RelatedResourceRowProps) {
  const lang = useLang('AI_CHAT');
  const sourceName = useSourceName(knowledgeItem);
  // The answer card's source line once the item resolves with a title; the server's title before.
  const title =
    (knowledgeItem?.title ? sourceName : '') || resource.title || lang.PANDA_RELATED_UNTITLED;

  return (
    <li className={cardStyles.section}>
      <div className={styles.titleLine}>
        <FontAwesomeIcon icon={faBooks} className={styles.icon} />
        <span className={styles.title}>{title}</span>
      </div>
      {knowledgeItem && (
        <SourceFooter
          chip={toContentTypeChip(knowledgeItem)}
          to={ROUTES.KNOWLEDGE_ITEMS.replace(':id', resource.documentId)}
          label={lang.PANDA_OPEN_SOURCE || 'Open'}
          ariaLabel={lang.PANDA_RELATED_OPEN_LABEL.replace('{title}', title)}
        />
      )}
    </li>
  );
}
